import React, { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Box, Typography, Button } from "@material-ui/core";
import { useProductFormStyles } from "./ProductForm.style";
import { Checkbox, IconButton } from "@material-ui/core";
import { ModusTextField } from "modus/components/form/text-field";
import { DeleteOutline } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { updateFormField, validateRedirectUrl } from "../../../../../store/actions/productFormActions";
import { RootState } from "../../../../../store/types";
import { PRODUCT_FORM_LIMITS, FEATURE_FLAGS, EXTERNAL_URLS } from "./constants";
import { APPLICATION_TYPES, APPLICATION_TYPE_GRANT_TYPES } from "../../TEXT_CONSTANTS";
import { Info } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";

/** Single application type from API (dynamic; no hardcoded keys). */
interface AppTypeOption {
  code: string;
  label: string;
  description: string;
  isRedirectUriRequired: boolean;
}

export const ApplicationType: React.FC = () => {
  const classes = useProductFormStyles();
  const dispatch = useDispatch();

  const { selectedApplicationTypeCodes, redirectUrlsByType, isEditing, product } = useSelector(
    (state: RootState) => state.productForm
  );
  const applicationTypesList = useSelector((state: RootState) => state.applicationTypesList ?? []);
  /** Only lock checkboxes when product is already public in the backend — not when user has selected public before save. */
  const isPublicPersisted = Boolean(
    isEditing && (product?.visibility ?? '').toLowerCase() === 'public'
  );

  /** Types already on the product when edit opened (from GET). Used to lock only those when public; newly checked types stay editable until save + refetch. */
  const persistedSelectedCodes = useMemo(() => {
    const types = product?.applicationTypes;
    if (!Array.isArray(types)) return new Set<string>();
    return new Set(
      types
        .map((item: { type?: string }) => item?.type)
        .filter((t: string | undefined): t is string => Boolean(t))
    );
  }, [product]);

  /**
   * Count of redirect URLs persisted per app type (from GET). When product is public, rows at
   * index &lt; count are read-only; new rows (Add another) stay editable.
   */
  const persistedRedirectUrlCountByCode = useMemo(() => {
    const map: Record<string, number> = {};
    const types = product?.applicationTypes;
    if (!Array.isArray(types)) return map;
    types.forEach((item: { type?: string; redirectUrls?: string[] }) => {
      if (!item?.type) return;
      const urls = item.redirectUrls;
      map[item.type] = Array.isArray(urls) ? urls.length : 0;
    });
    return map;
  }, [product]);

  const options: AppTypeOption[] = useMemo(() => {
    if (!applicationTypesList || applicationTypesList.length === 0) return [];
    return applicationTypesList
      .filter((item) => FEATURE_FLAGS.SHOW_EXTENSION_TYPE || item.applicationType !== 'EXTENSION')
      .map((item) => {
        const code = item.applicationType;
        const grantType = item.grant_type;
        const label =
          (code && (APPLICATION_TYPES as Record<string, string>)[code]) ??
          item.name ??
          code ??
          "";
        const description =
          (grantType && (APPLICATION_TYPE_GRANT_TYPES as Record<string, string>)[grantType]) ??
          grantType ??
          "";
        return {
          code: code ?? "",
          label,
          description,
          isRedirectUriRequired: Boolean(item.isRedirectUriRequired)
        };
      });
  }, [applicationTypesList]);

  const selectedSet = useMemo(
    () => new Set(selectedApplicationTypeCodes.value),
    [selectedApplicationTypeCodes.value]
  );

  const [urlErrorsByType, setUrlErrorsByType] = useState<Record<string, Record<number, string | null>>>({});

  const handleApplicationTypeChange = useCallback(
    (code: string, checked: boolean) => {
      if (isPublicPersisted && persistedSelectedCodes.has(code) && !checked) return;
      const next = checked
        ? [...selectedApplicationTypeCodes.value, code]
        : selectedApplicationTypeCodes.value.filter((c) => c !== code);
      dispatch(updateFormField("selectedApplicationTypeCodes", next, true, null));
      if (!checked) {
        const nextByType = { ...redirectUrlsByType.value };
        delete nextByType[code];
        dispatch(updateFormField("redirectUrlsByType", nextByType, true, null));
        setUrlErrorsByType((prev) => {
          const nextErr = { ...prev };
          delete nextErr[code];
          return nextErr;
        });
      }
    },
    [dispatch, selectedApplicationTypeCodes.value, redirectUrlsByType.value, isPublicPersisted, persistedSelectedCodes]
  );

  const ensureRedirectUrlsForCode = useCallback(
    (code: string) => {
      const urls = redirectUrlsByType.value[code];
      if (urls === undefined || urls.length === 0) {
        dispatch(
          updateFormField("redirectUrlsByType", { ...redirectUrlsByType.value, [code]: [""] }, true, null)
        );
      }
    },
    [dispatch, redirectUrlsByType.value]
  );

  useEffect(() => {
    options.forEach((opt) => {
      if (opt.isRedirectUriRequired && selectedSet.has(opt.code)) {
        ensureRedirectUrlsForCode(opt.code);
      }
    });
  }, [options, selectedSet, ensureRedirectUrlsForCode]);

  const addRedirectUrl = useCallback(
    (code: string) => {
      const urls = redirectUrlsByType.value[code] ?? [""];
      if (urls.length >= PRODUCT_FORM_LIMITS.MAX_REDIRECT_URLS) return;
      dispatch(
        updateFormField("redirectUrlsByType", {
          ...redirectUrlsByType.value,
          [code]: [...urls, ""]
        }, true, null)
      );
    },
    [dispatch, redirectUrlsByType.value]
  );

  const removeRedirectUrl = useCallback(
    (code: string, index: number) => {
      if (isPublicPersisted) {
        const persistedCount = persistedRedirectUrlCountByCode[code] ?? 0;
        if (index < persistedCount) return;
      }
      const urls = (redirectUrlsByType.value[code] ?? []).filter((_, i) => i !== index);
      const nextByType = { ...redirectUrlsByType.value };
      if (urls.length === 0) delete nextByType[code];
      else nextByType[code] = urls;
      dispatch(updateFormField("redirectUrlsByType", nextByType, true, null));
      setUrlErrorsByType((prev) => {
        const next = { ...prev };
        if (next[code]) {
          const errs = { ...next[code] };
          delete errs[index];
          next[code] = errs;
        }
        return next;
      });
    },
    [dispatch, redirectUrlsByType.value, isPublicPersisted, persistedRedirectUrlCountByCode]
  );

  const updateRedirectUrl = useCallback(
    (code: string, index: number, value: string) => {
      if (isPublicPersisted) {
        const persistedCount = persistedRedirectUrlCountByCode[code] ?? 0;
        if (index < persistedCount) return;
      }
      const urls = [...(redirectUrlsByType.value[code] ?? [])];
      urls[index] = value;
      const nextByType = { ...redirectUrlsByType.value, [code]: urls };
      let allValid = true;
      const newErrors: Record<number, string | null> = {};
      urls.forEach((url, i) => {
        const v = validateRedirectUrl(url);
        if (!v.valid) {
          newErrors[i] = v.error;
          allValid = false;
        } else if (url.trim() !== "") {
          const trimmed = url.trim().toLowerCase();
          const dup = urls.some((o, j) => j < i && o.trim().toLowerCase() === trimmed);
          if (dup) {
            newErrors[i] = "This redirect URL has already been added";
            allValid = false;
          }
        }
      });
      setUrlErrorsByType((prev) => ({ ...prev, [code]: newErrors }));
      dispatch(updateFormField("redirectUrlsByType", nextByType, allValid, null));
      dispatch(updateFormField("redirectUrlsValid", allValid, allValid, null));
    },
    [dispatch, redirectUrlsByType.value, isPublicPersisted, persistedRedirectUrlCountByCode]
  );

  const selectedTypesRequiringRedirect = useMemo(
    () => options.filter((o) => o.isRedirectUriRequired && selectedSet.has(o.code)),
    [options, selectedSet]
  );

  // Keep redirectUrlsValid in sync: for each selected type with isRedirectUriRequired, validate URLs
  useEffect(() => {
    let allValid = true;
    for (const opt of selectedTypesRequiringRedirect) {
      const urls = redirectUrlsByType.value[opt.code] ?? [];
      const seen = new Set<string>();
      for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        if (url.trim() === "") continue;
        const {valid} = validateRedirectUrl(url);
        if (!valid) {
          allValid = false;
          break;
        }
        const key = url.trim().toLowerCase();
        if (seen.has(key)) {
          allValid = false;
          break;
        }
        seen.add(key);
      }
      if (!allValid) break;
    }
    dispatch(updateFormField("redirectUrlsValid", allValid, allValid, null));
  }, [selectedTypesRequiringRedirect, redirectUrlsByType.value, dispatch]);

  if (!FEATURE_FLAGS.PRODUCT_METADATA_CONFIG) {
    return null;
  }

  return (
    <>
      <Box>
        <Typography className={classes.sectionTitle}>Application types</Typography>
        {EXTERNAL_URLS.CUSTOM_APP_TYPE_FORM && (
          <Box display="flex" alignItems="center" marginBottom="16px" style={{ gap: "6px" }}>
            <Info style={{ fontSize: "18px", color: "#6A6E79" }} />
            <Typography variant="body2" style={{ color: "#6A6E79", fontSize: "14px" }}>
              Need a custom application type?{" "}
              <a
                href={EXTERNAL_URLS.CUSTOM_APP_TYPE_FORM}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#0063a3", textDecoration: "none" }}
              >
                Request one
              </a>
            </Typography>
          </Box>
        )}
        <Box className={classes.checkboxGroup}>
          {options.map((option) => {
            const urls = redirectUrlsByType.value[option.code] ?? [""];
            const errors = urlErrorsByType[option.code] ?? {};
            const showRedirect = option.isRedirectUriRequired && selectedSet.has(option.code);
            const isChecked = selectedSet.has(option.code);
            const isCheckboxDisabled =
              isPublicPersisted && isChecked && persistedSelectedCodes.has(option.code);
            return (
              <Box key={option.code}>
                <Box className={classes.checkboxOption}>
                  <Box className={classes.checkboxContent}>
                    <Checkbox
                      checked={isChecked}
                      color="primary"
                      disabled={isCheckboxDisabled}
                      onChange={(e) => handleApplicationTypeChange(option.code, e.target.checked)}
                      className={clsx(
                        classes.checkboxControl,
                        isCheckboxDisabled && classes.checkboxControlLockedPublic
                      )}
                    />
                    <Box className={classes.checkboxText}>
                      <Typography
                        className={clsx(
                          classes.checkboxLabel,
                          isCheckboxDisabled && classes.checkboxLabelLockedPublic
                        )}
                      >
                        {option.label}
                      </Typography>
                      <Typography
                        className={clsx(
                          classes.checkboxDescription,
                          isCheckboxDisabled && classes.checkboxDescriptionLockedPublic
                        )}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                {showRedirect && (
                  <Box className={classes.configContainer}>
                    <div className={classes.configLine} />
                    <Box className={classes.configContent}>
                      <Typography className={classes.sectionTitle}>
                        Redirect URLs
                      </Typography>
                      <Box className={classes.redirectUrlsContainer}>
                        {urls.map((url, index) => {
                          const persistedCount =
                            persistedRedirectUrlCountByCode[option.code] ?? 0;
                          const isPersistedRedirectRow =
                            isPublicPersisted && index < persistedCount;
                          return (
                          <Box key={`${option.code}-${index}`} className={classes.redirectUrlRow}>
                            <ModusTextField
                              value={url}
                              onChange={(e) => updateRedirectUrl(option.code, index, e.target.value)}
                              placeholder="https://example.com"
                              name={`redirect-url-${option.code}-${index}`}
                              className={classes.redirectUrlInput}
                              error={Boolean(errors[index])}
                              helperText={errors[index] ?? undefined}
                              disabled={isPersistedRedirectRow}
                              validateInitiallyTouched
                            />
                            <IconButton
                              onClick={() => removeRedirectUrl(option.code, index)}
                              className={classes.deleteButton}
                              size="small"
                              disabled={isPersistedRedirectRow}
                            >
                              <DeleteOutline />
                            </IconButton>
                          </Box>
                          );
                        })}
                        {urls.length >= PRODUCT_FORM_LIMITS.MAX_REDIRECT_URLS && (
                          <Alert variant="outlined" severity="info" className={classes.maxUrlsNotice}>
                            <Typography variant="body2">
                              Maximum limit of {PRODUCT_FORM_LIMITS.MAX_REDIRECT_URLS} redirect URLs reached.
                            </Typography>
                          </Alert>
                        )}
                        <Button
                          onClick={() => addRedirectUrl(option.code)}
                          variant="text"
                          color="primary"
                          className={classes.addAnotherButton}
                          disabled={urls.length >= PRODUCT_FORM_LIMITS.MAX_REDIRECT_URLS}
                        >
                          + Add another
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
};
